import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, LogOut, ChevronDown, Rocket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NavHeader = () => {
  const location = useLocation();
  const { user, isAuthenticated, loginWithGoogle, logout } = useAuth();
  const [claimedProfile, setClaimedProfile] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Fetch claimed profile when authenticated
  useEffect(() => {
    const fetchClaimedProfile = async () => {
      if (!isAuthenticated) {
        setClaimedProfile(null);
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/me/claimed-profile`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setClaimedProfile(data.profile);
        }
      } catch (err) {
        console.error('Failed to fetch claimed profile:', err);
      }
    };
    
    fetchClaimedProfile();
  }, [isAuthenticated]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav className="bg-slate-950 border-b border-slate-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Rocket icon like original */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white hidden sm:block">The Founder Initiative</span>
          </Link>
          
          {/* Main Nav */}
          <div className="flex items-center gap-6">
            <Link 
              to="/buyers" 
              className={`transition-colors text-sm ${isActive('/buyers') ? 'text-white font-medium' : 'text-slate-400 hover:text-white'}`}
            >
              Buyers
            </Link>
            <Link 
              to="/sponsors" 
              className={`transition-colors text-sm ${isActive('/sponsors') ? 'text-white font-medium' : 'text-slate-400 hover:text-white'}`}
            >
              Sponsors
            </Link>
            <Link 
              to="/podcast" 
              className={`transition-colors text-sm ${isActive('/podcast') ? 'text-white font-medium' : 'text-slate-400 hover:text-white'}`}
            >
              Podcast
            </Link>
            <Link 
              to="/buyer-pitches" 
              className={`transition-colors text-sm ${isActive('/buyer-pitches') ? 'text-white font-medium' : 'text-slate-400 hover:text-white'}`}
            >
              Buyer Pitches
            </Link>
            
            {/* My Profile - only show if user has claimed a profile */}
            {claimedProfile && (
              <Link 
                to={`/startups/${claimedProfile.slug}`}
                className={`transition-colors text-sm flex items-center gap-1.5 ${
                  location.pathname === `/startups/${claimedProfile.slug}` 
                    ? 'text-blue-400 font-medium' 
                    : 'text-blue-400/70 hover:text-blue-400'
                }`}
                data-testid="my-profile-link"
              >
                <User className="w-3.5 h-3.5" />
                My Profile
              </Link>
            )}
            
            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                  data-testid="user-menu-btn"
                >
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                      <span className="text-xs font-bold text-white">{user?.name?.[0] || '?'}</span>
                    </div>
                  )}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                
                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowUserMenu(false)} 
                    />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-slate-900 border border-slate-800 rounded-lg shadow-xl z-50 py-1">
                      <div className="px-3 py-2 border-b border-slate-800">
                        <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                        <p className="text-slate-500 text-xs truncate">{user?.email}</p>
                      </div>
                      {claimedProfile && (
                        <Link
                          to={`/startups/${claimedProfile.slug}`}
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
                        >
                          <User className="w-4 h-4" />
                          My Profile
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white w-full"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <a
                href="https://app.thefounderinitiative.com"
                className="text-sm px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                data-testid="login-btn"
              >
                Login
              </a>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavHeader;
